import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';

interface UserRoleChangeConfirmProps {
  open: boolean;
  email: string;
  currentRole: string;
  targetRole: string;
  isSelf: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function UserRoleChangeConfirm({
  open,
  email,
  currentRole,
  targetRole,
  isSelf,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage,
}: UserRoleChangeConfirmProps) {
  const { t } = useTranslation();

  // R8-A-011: use i18n keys from _shared appshell.user.role.* for locale-aware role labels
  const toRoleLabel = (role: string) =>
    t(`appshell.user.role.${role.replace('ROLE_', '').toLowerCase()}`, { defaultValue: role });

  return (
    <FormDialog
      open={open}
      title={t('user.action.confirm.changeRole.title')}
      onClose={onClose}
      onSubmit={isSelf ? () => {} : onConfirm}
      submitLabel={isLoading ? t('user.action.confirm.ok') + '...' : t('user.action.confirm.ok')}
      cancelLabel={t('user.action.confirm.cancel')}
    >
      <div>
        {isSelf ? (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-warning)',
            }}
          >
            {t('user.detail.role.selfDisabled')}
          </div>
        ) : (
          <>
            {errorMessage && (
              <div
                role="alert"
                style={{
                  marginBottom: 'var(--spacing-3)',
                  padding: 'var(--spacing-3)',
                  background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-danger)',
                }}
              >
                {errorMessage}
              </div>
            )}
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-3)' }}>
              {t('user.action.confirm.changeRole.description', {
                email,
                from: toRoleLabel(currentRole),
                to: toRoleLabel(targetRole),
              })}
            </p>
            <div
              style={{
                padding: 'var(--spacing-3)',
                background: 'color-mix(in srgb, var(--color-warning) 8%, var(--color-surface-muted))',
                border: '1px solid var(--color-warning)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('user.toast.roleChanged')}
            </div>
          </>
        )}
      </div>
    </FormDialog>
  );
}
